import React, { useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import AnalysisTablePalette from '../components/AnalysisTablePalette';
import './TipsTest.css';

const AYLAR = [
    'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
    'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık',
];

const formatTarih = (value) => {
    if (!value || value.length < 10) return value || '';
    const year = value.slice(0, 4);
    const month = parseInt(value.slice(5, 7), 10);
    const day = parseInt(value.slice(8, 10), 10);
    if (!month || !day) return value;
    return `${day} ${AYLAR[month - 1]} ${year}`;
};

const STAT_LABELS = {
    iy05: 'İY 0.5 Üst',
    iy15: 'İY 1.5 Üst',
    ms25: 'MS 2.5 Üst',
    ms35: 'MS 3.5 Üst',
    kgv: 'KG Var',
};

const INPUTS = [
    ['o1', '1 Oranı'],
    ['ox', 'X Oranı'],
    ['o2', '2 Oranı'],
    ['over25', '2.5 Üst'],
    ['under25', '2.5 Alt'],
];

const authHeaders = (withJson = false) => {
    const headers = {};
    const token = localStorage.getItem('sessionToken');
    if (token) headers.Authorization = `Bearer ${token}`;
    if (withJson) headers['Content-Type'] = 'application/json';
    return headers;
};

const TrendCard = ({ title, items }) => (
    <div className="nexora-trend-card">
        <h5>{title}</h5>
        {items.map((item, index) => (
            <div className="nexora-trend-row" key={item.score}>
                <span className={`nexora-trend-dot ${item.signal}`}></span>
                <strong>{item.score}</strong>
                <span>{item.count} maç</span>
                <em>%{item.percentage}</em>
            </div>
        ))}
    </div>
);

const formatOdd = (value) => (
    value !== null && value !== undefined && Number.isFinite(Number(value))
        ? Number(value).toFixed(2)
        : '—'
);

const NexoraAnaliz = () => {
    const [mode, setMode] = useState('manual');
    const [sources, setSources] = useState([]);
    const [source, setSource] = useState('');
    const [matches, setMatches] = useState([]);
    const [selectedId, setSelectedId] = useState('');
    const [matchesLoading, setMatchesLoading] = useState(false);
    const [odds, setOdds] = useState({
        o1: '', ox: '', o2: '', over25: '', under25: '',
    });
    const [excludeSterile, setExcludeSterile] = useState(false);
    const [loadingSources, setLoadingSources] = useState(true);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [result, setResult] = useState(null);

    useEffect(() => {
        let cancelled = false;
        const loadSources = async () => {
            setLoadingSources(true);
            try {
                const response = await fetch('/api/nexora/sources', {
                    headers: authHeaders(),
                });
                const payload = await response.json();
                if (!response.ok) throw new Error(payload.error || 'Veri kaynakları yüklenemedi.');
                if (cancelled) return;
                setSources(payload);
                const firstAvailable = payload.find((item) => item.available);
                if (firstAvailable) setSource(firstAvailable.key);
                else setError('Nexora için kullanılabilir oran tablosu bulunamadı.');
            } catch (loadError) {
                if (!cancelled) setError(loadError.message || 'Veri kaynakları yüklenemedi.');
            } finally {
                if (!cancelled) setLoadingSources(false);
            }
        };
        loadSources();
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        if (mode !== 'match' || !source) return undefined;
        let cancelled = false;
        setMatchesLoading(true);
        setMatches([]);
        setSelectedId('');
        setResult(null);
        setError('');

        fetch(`/api/nexora/matches?source=${encodeURIComponent(source)}`, {
            headers: authHeaders(),
        })
            .then(async (response) => {
                const payload = await response.json();
                if (!response.ok) throw new Error(payload.error || 'Maçlar yüklenemedi.');
                return payload;
            })
            .then((payload) => {
                if (!cancelled) setMatches(Array.isArray(payload.matches) ? payload.matches : []);
            })
            .catch((loadError) => {
                if (!cancelled) setError(loadError.message || 'Maçlar yüklenemedi.');
            })
            .finally(() => {
                if (!cancelled) setMatchesLoading(false);
            });

        return () => { cancelled = true; };
    }, [mode, source]);

    const selectedMatch = useMemo(
        () => matches.find((match) => String(match.id) === selectedId),
        [matches, selectedId]
    );

    const matchOptions = useMemo(() => matches.map((match) => ({
        value: String(match.id),
        label: `${formatTarih(match.tarih)}${match.saat ? ` · ${match.saat}` : ''}`
            + `${match.ulke ? ` | ${match.ulke}` : ''}`
            + ` | ${match.ev} - ${match.dep} (${match.o1} / ${match.ox} / ${match.o2})`,
        match,
    })), [matches]);

    const selectedOption = selectedMatch ? matchOptions.find(
        (option) => option.value === String(selectedMatch.id)
    ) : null;

    const updateOdd = (key, value) => {
        setOdds((current) => ({ ...current, [key]: value }));
        setResult(null);
    };

    const analyze = async (event) => {
        event.preventDefault();
        if (!source) {
            setError('Veri kaynağını seçin.');
            return;
        }
        if (mode === 'match' && !selectedId) {
            setError('Analiz edilecek maçı seçin.');
            return;
        }
        if (mode === 'manual' && Object.values(odds).some((value) => !value.trim())) {
            setError('Veri kaynağını ve tüm oranları girin.');
            return;
        }

        const normalizedOdds = Object.fromEntries(
            Object.entries(odds).map(([key, value]) => [key, value.replace(',', '.')])
        );
        if (mode === 'manual' && Object.values(normalizedOdds).some(
            (value) => !Number.isFinite(Number(value)) || Number(value) <= 1
        )) {
            setError('Oranların tamamı 1.00’dan büyük olmalıdır.');
            return;
        }

        setLoading(true);
        setError('');
        setResult(null);
        try {
            const response = await fetch('/api/nexora/analyze', {
                method: 'POST',
                headers: authHeaders(true),
                body: JSON.stringify({
                    source,
                    ...(mode === 'match' ? { match_id: selectedId } : normalizedOdds),
                    exclude_sterile: excludeSterile,
                }),
            });
            const payload = await response.json();
            if (!response.ok) throw new Error(payload.error || 'Analiz sırasında bir hata oluştu.');
            setResult(payload);
        } catch (analysisError) {
            setError(analysisError.message || 'Sunucuya ulaşılamadı.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="tips-lab-page nexora-page">
            <div className="breadcrumb-section breadcrumb-bg">
                <div className="container">
                    <div className="row">
                        <div className="col-lg-8 offset-lg-2 text-center">
                            <div className="breadcrumb-text">
                                <p>Oran Analizi</p>
                                <h1>Nexora Analiz</h1>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="tips-lab-content">
                <div className="container">
                    <div className="row">
                        <div className="col-lg-12">
                            <div className="section-title text-center">
                                <h3>Nexora <span className="orange-text">Analiz</span></h3>
                                <p>Oranları kendiniz girin veya takım adıyla maç seçerek analiz sonucunu görüntüleyin.</p>
                            </div>

                            <form className="tipstest-panel nexora-panel" onSubmit={analyze}>
                                <div className="tt2-tabs nexora-mode-tabs" role="tablist" aria-label="Analiz yöntemi">
                                    <button
                                        type="button"
                                        role="tab"
                                        aria-selected={mode === 'manual'}
                                        className={`tt2-tab ${mode === 'manual' ? 'tt2-tab-active' : ''}`}
                                        onClick={() => {
                                            setMode('manual');
                                            setResult(null);
                                            setError('');
                                        }}
                                    >
                                        Oran Gir
                                    </button>
                                    <button
                                        type="button"
                                        role="tab"
                                        aria-selected={mode === 'match'}
                                        className={`tt2-tab ${mode === 'match' ? 'tt2-tab-active' : ''}`}
                                        onClick={() => {
                                            setMode('match');
                                            setResult(null);
                                            setError('');
                                        }}
                                    >
                                        Maç Seç
                                    </button>
                                </div>

                                <div className="nexora-source-row">
                                    <div className="tt2-select-field">
                                        <label htmlFor="nexora-source">Veri Kaynağı</label>
                                        <select
                                            id="nexora-source"
                                            value={source}
                                            onChange={(event) => {
                                                setSource(event.target.value);
                                                setSelectedId('');
                                                setResult(null);
                                            }}
                                            disabled={loadingSources}
                                        >
                                            <option value="">
                                                {loadingSources ? 'Kaynaklar yükleniyor...' : 'Kaynak seçin...'}
                                            </option>
                                            {sources.map((item) => (
                                                <option key={item.key} value={item.key} disabled={!item.available}>
                                                    {item.name}{item.available ? '' : ' (uygun veri yok)'}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <label className="nexora-toggle">
                                        <input
                                            type="checkbox"
                                            checked={excludeSterile}
                                            onChange={(event) => {
                                                setExcludeSterile(event.target.checked);
                                                setResult(null);
                                            }}
                                        />
                                        <span>
                                            <strong>Lig filtresi</strong>
                                            <small>Belirli düşük skorlu ligleri hariç tut</small>
                                        </span>
                                    </label>
                                </div>

                                {mode === 'manual' ? (
                                    <div className="nexora-odds-grid">
                                        {INPUTS.map(([key, label]) => (
                                            <div className="tipstest-field" key={key}>
                                                <label htmlFor={`nexora-${key}`}>{label}</label>
                                                <input
                                                    id={`nexora-${key}`}
                                                    type="text"
                                                    inputMode="decimal"
                                                    placeholder="0.00"
                                                    value={odds[key]}
                                                    onChange={(event) => updateOdd(key, event.target.value)}
                                                    autoComplete="off"
                                                />
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="nexora-match-picker">
                                        <div className="tt2-select-field tt2-match-field">
                                            <label htmlFor="nexora-match">Takım veya Maç Seç</label>
                                            <Select
                                                inputId="nexora-match"
                                                value={selectedOption}
                                                onChange={(option) => {
                                                    setSelectedId(option ? option.value : '');
                                                    setResult(null);
                                                }}
                                                options={matchOptions}
                                                isClearable
                                                isSearchable
                                                isDisabled={matchesLoading || !source}
                                                placeholder={matchesLoading
                                                    ? 'Maçlar yükleniyor...'
                                                    : matches.length
                                                        ? 'Takım adı yazarak arayın...'
                                                        : 'Yaklaşan maç bulunamadı'}
                                                noOptionsMessage={() => 'Maç bulunamadı'}
                                                classNamePrefix="tt1-react-select"
                                            />
                                        </div>

                                        {selectedMatch && (
                                            <div className="nexora-selected-match">
                                                <div>
                                                    <span>{formatTarih(selectedMatch.tarih)}{selectedMatch.saat ? ` · ${selectedMatch.saat}` : ''}</span>
                                                    <strong>{selectedMatch.ev} - {selectedMatch.dep}</strong>
                                                    {selectedMatch.ulke && <small>{selectedMatch.ulke}</small>}
                                                </div>
                                                <p>
                                                    1 <b>{selectedMatch.o1}</b>
                                                    <span>X <b>{selectedMatch.ox}</b></span>
                                                    <span>2 <b>{selectedMatch.o2}</b></span>
                                                    <span>Üst <b>{selectedMatch.over25}</b></span>
                                                    <span>Alt <b>{selectedMatch.under25}</b></span>
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                )}

                                <div className="tipstest-actions">
                                    <button
                                        className="boxed-btn"
                                        type="submit"
                                        disabled={loading || loadingSources || matchesLoading || !source || (mode === 'match' && !selectedId)}
                                    >
                                        {loading ? 'Analiz ediliyor...' : 'Analiz Et'}
                                    </button>
                                </div>
                                {error && <div className="tipstest-error">{error}</div>}
                            </form>

                            {result && (
                                <div className="tipstest-results nexora-results">
                                    <div className="tt1-result-header">
                                        <div>
                                            <span className="tt2-eyebrow">Nexora Analiz</span>
                                            <h4>{result.sample_size} Maçlık İstatistik Paneli</h4>
                                        </div>
                                        <span className="nexora-source-badge">{result.source.name}</span>
                                    </div>

                                    {result.selected_match && (
                                        <div className="tipstest-matchup nexora-result-matchup">
                                            <span className="tipstest-team-name">{result.selected_match.ev}</span>
                                            <span className="tipstest-matchup-vs">vs</span>
                                            <span className="tipstest-team-name">{result.selected_match.dep}</span>
                                        </div>
                                    )}

                                    <div className={`nexora-prediction ${result.prediction.tone}`}>
                                        <span>Tahmin</span>
                                        <strong>{result.prediction.label}</strong>
                                    </div>

                                    {result.reversal.warning && (
                                        <div className="nexora-warning">
                                            <strong>Dikkat</strong>
                                            Bu oran havuzunda %{result.reversal.percentage} maç içi yön değişimi görüldü.
                                        </div>
                                    )}

                                    <div className="tt1-stats-grid nexora-stats-grid">
                                        {Object.entries(result.stats).map(([key, stat]) => (
                                            <div className="tt1-stat-card" key={key}>
                                                <span>{STAT_LABELS[key]}</span>
                                                <strong>%{stat.percentage}</strong>
                                                <small>{stat.success}/{stat.n} maç</small>
                                            </div>
                                        ))}
                                    </div>

                                    <h4 className="tt2-bolum-baslik">Geçmiş Maçlar</h4>
                                    <AnalysisTablePalette>
                                        <table className="table nexora-table">
                                            <thead>
                                                <tr>
                                                    <th>Tarih</th><th>Lig</th><th>Maç</th>
                                                    <th>İY</th><th>MS</th><th>İY/MS</th>
                                                    <th>1</th><th>X</th><th>2</th>
                                                    <th>2.5 Alt</th><th>2.5 Üst</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {result.matches.map((match) => (
                                                    <tr key={`${match.id}-${match.tarih}`}>
                                                        <td>{match.tarih || '—'}</td>
                                                        <td>{match.lig || '—'}</td>
                                                        <td className="tt2-mac-adi">
                                                            {match.ev} - {match.dep}
                                                            {match.hot && <span className="nexora-match-badge">🔥</span>}
                                                            {match.rocket && <span className="nexora-match-badge">🚀</span>}
                                                        </td>
                                                        <td>{match.iy}</td>
                                                        <td><strong>{match.ms}</strong></td>
                                                        <td>{match.htft}</td>
                                                        <td className="nexora-odd-value">{formatOdd(match.o1)}</td>
                                                        <td className="nexora-odd-value">{formatOdd(match.ox)}</td>
                                                        <td className="nexora-odd-value">{formatOdd(match.o2)}</td>
                                                        <td className="nexora-odd-value">{formatOdd(match.under25)}</td>
                                                        <td className="nexora-odd-value">{formatOdd(match.over25)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </AnalysisTablePalette>

                                    <div className="nexora-trends-grid">
                                        <TrendCard title="İlk Yarı Skor Trendi" items={result.trends.ht} />
                                        <TrendCard title="Maç Sonu Skor Trendi" items={result.trends.ft} />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default NexoraAnaliz;
